import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, ArrowLeft, Loader2, Monitor, Tablet, Smartphone, Upload, Palette, Layout,
  Trash2, ChevronUp, ChevronDown, Copy, Code, Check, Type, Image as ImageIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SIGNATURE_TEMPLATES, ICONS } from "./templates";
import { takeSignatureBuilderIntent } from "./pendingIntent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_NEW_FIELD = {
  type: "text",
  label: "",
  value: "",
  icon: "none",
  buttonStyle: "solid",
  buttonColor: "#000000",
  buttonTextColor: "#ffffff",
  showLabel: true,
  showIcon: true,
  imageWidth: "100",
  imageHeight: "100",
};

export default function SignatureEditor({ editId, basePath = "/account/signatures" }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [copyStatus, setCopyStatus] = useState("none"); // none | signature | code

  const [showCustomFieldForm, setShowCustomFieldForm] = useState(false);
  const [newField, setNewField] = useState(EMPTY_NEW_FIELD);

  const [name, setName] = useState("New Signature");
  const [templateId, setTemplateId] = useState("professional-modern");
  const [colors, setColors] = useState({
    primary: "#0066cc",
    secondary: "#666666",
    accent: "#000000",
  });
  const [fields, setFields] = useState({
    fullName: "Natalie Lynch",
    title: "Sales Representative",
    company: "Company",
    phone: "202-555-0141",
    email: "natalie.lynch@example.com",
    website: "example.com",
    facebook: "",
    linkedin: "natalielynch",
    twitter: "",
    instagram: "",
    youtube: "",
    logoUrl: "",
    avatarUrl: "",
    bannerUrl: "",
    disclaimer: "This email and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed. If you have received this email in error, please notify us immediately and delete the message from your system.",
    bottomLinks: "",
    banner: "",
  });

  const [fieldOrder, setFieldOrder] = useState([]);

  useEffect(() => {
    // Exclude special fields from the reorderable list.
    const orderableFields = Object.keys(fields).filter(k =>
      !["logoUrl", "avatarUrl", "bannerUrl", "banner", "disclaimer", "bottomLinks", "customFields"].includes(k)
    );
    setFieldOrder(prev => prev.length > 0 ? prev : orderableFields);
  }, [fields]);

  const moveField = (index, direction) => {
    const newOrder = [...fieldOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
      setFieldOrder(newOrder);
    }
  };

  const [previewMode, setPreviewMode] = useState("desktop"); // desktop | tablet | mobile
  const [activeTab, setActiveTab] = useState("content"); // content | templates | style

  useEffect(() => {
    takeSignatureBuilderIntent();
    if (editId) fetchSignature();
  }, [editId]);

  const fetchSignature = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/signatures/${editId}`);
      const sig = res.data;
      setName(sig.name);
      setFields(prev => ({ ...prev, ...sig.fields }));
      if (sig.field_order && sig.field_order.length > 0) {
        setFieldOrder(sig.field_order);
      }
      setTemplateId(sig.template_id || "modern");
      if (sig.colors && Object.keys(sig.colors).length > 0) {
        setColors(sig.colors);
      }
    } catch (err) {
      toast.error("Could not load that signature");
      navigate(basePath);
    } finally {
      setIsLoading(false);
    }
  };

  const currentTemplate = SIGNATURE_TEMPLATES.find(t => t.id === templateId) || SIGNATURE_TEMPLATES[0];

  const [uploadingCustomIndex, setUploadingCustomIndex] = useState(null);

  // The upload endpoint hands back a path on our own API (e.g.
  // /api/storage/public/...), not a full URL. A pasted signature leaves our
  // site entirely, so every image src in it has to be absolute.
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "signature-assets");
    try {
      const res = await axios.post(`${API}/storage/upload-customization`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url;
      return url.startsWith("/") ? `${BACKEND_URL}${url}` : url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      return null;
    }
  };

  const handleCustomFileUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (index !== undefined) setUploadingCustomIndex(index);
    else setIsUploading(true);

    const url = await uploadFile(file);

    if (url) {
      if (index !== undefined && fields.customFields) {
        const updated = [...fields.customFields];
        updated[index].value = url;
        setFields(prev => ({ ...prev, customFields: updated }));
      } else {
        setNewField(prev => ({ ...prev, value: url }));
      }
    }

    if (index !== undefined) setUploadingCustomIndex(null);
    else setIsUploading(false);

    if (e.target) e.target.value = "";
  };

  const handleFileDrop = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (type === 'logo' || type === 'avatar' || type === 'bannerUrl') {
      setIsUploading(true);
      const url = await uploadFile(file);
      if (url) {
        setFields(prev => ({ ...prev, [type === 'bannerUrl' ? 'bannerUrl' : (type === 'avatar' ? 'avatarUrl' : 'logoUrl')]: url }));
      }
      setIsUploading(false);
    } else {
      const index = typeof type === 'number' ? type : undefined;
      if (index !== undefined) setUploadingCustomIndex(index);
      else setIsUploading(true);

      const url = await uploadFile(file);
      if (url) {
        if (index !== undefined && fields.customFields) {
          const updated = [...fields.customFields];
          updated[index].value = url;
          setFields(prev => ({ ...prev, customFields: updated }));
        } else {
          setNewField(prev => ({ ...prev, value: url }));
        }
      }

      if (index !== undefined) setUploadingCustomIndex(null);
      else setIsUploading(false);
    }
  };

  const preventDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const genericUpload = async (file, fieldPath) => {
    setIsUploading(true);
    const url = await uploadFile(file);
    if (url) {
      setFields(prev => ({ ...prev, [fieldPath]: url }));
    }
    setIsUploading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        name,
        fields,
        field_order: fieldOrder,
        template_id: templateId,
        colors,
        html_content: currentTemplate.render(fields, colors, fieldOrder),
      };

      if (editId) {
        await axios.put(`${API}/signatures/${editId}`, payload);
      } else {
        await axios.post(`${API}/signatures`, payload);
      }
      toast.success("Signature saved");
      navigate(basePath);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save signature. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const copySignature = async () => {
    const html = currentTemplate.render(fields, colors, fieldOrder);

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([html.replace(/<[^>]*>?/gm, '')], { type: "text/plain" });
        const data = [new ClipboardItem({ "text/html": blob, "text/plain": textBlob })];
        await navigator.clipboard.write(data);
        setCopyStatus("signature");
        setTimeout(() => setCopyStatus("none"), 2000);
        return;
      }
    } catch (err) {
      console.warn("Modern clipboard write failed, trying fallback...", err);
    }

    // Fallback using standard DOM selection (Firefox and older browsers).
    try {
      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);

      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(container);
      selection?.removeAllRanges();
      selection?.addRange(range);

      document.execCommand("copy");

      selection?.removeAllRanges();
      document.body.removeChild(container);

      setCopyStatus("signature");
      setTimeout(() => setCopyStatus("none"), 2000);
    } catch (e) {
      console.error("Fallback copy failed:", e);
      toast.error("Copy failed. Please try a different browser or use Copy HTML Code.");
    }
  };

  const copyHTMLCode = async () => {
    const html = currentTemplate.render(fields, colors, fieldOrder);
    try {
      await navigator.clipboard.writeText(html);
      setCopyStatus("code");
      setTimeout(() => setCopyStatus("none"), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const handleFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="flex flex-col gap-6 h-[calc(100vh-140px)]">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(basePath)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="font-bold text-lg bg-transparent border-none focus:ring-0 w-64"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-black text-white px-6 py-2.5 rounded-2xl font-bold flex items-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Signature
        </button>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Editor Sidebar */}
        <div className="w-80 bg-white rounded-3xl border border-gray-100 flex flex-col overflow-hidden shadow-sm relative">
          <div className="flex border-b border-gray-100">
            {[
              { id: "content", icon: Upload, label: "Content" },
              { id: "templates", icon: Layout, label: "Design" },
              { id: "style", icon: Palette, label: "Style" },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 flex flex-col items-center gap-1 border-b-2 transition-all ${activeTab === tab.id ? "border-black text-black" : "border-transparent text-gray-400"}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "content" && (
              <div className="space-y-4">
                <div className="flex gap-4 mb-6">
                  <div className="flex-1">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Company Logo</label>
                    <div
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = e.target.files?.[0];
                          if (file) genericUpload(file, 'logoUrl');
                        };
                        input.click();
                      }}
                      onDragOver={preventDrag}
                      onDragEnter={preventDrag}
                      onDragLeave={preventDrag}
                      onDrop={(e) => handleFileDrop(e, 'logo')}
                      className="aspect-square bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all group overflow-hidden relative"
                    >
                      {isUploading ? (
                        <Loader2 className="animate-spin text-black" />
                      ) : fields.logoUrl ? (
                        <>
                          <img
                            key={fields.logoUrl}
                            src={fields.logoUrl}
                            alt="Logo preview"
                            className="w-full h-full object-contain p-4 group-hover:opacity-50 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Upload className="text-black" />
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs font-bold uppercase">Upload</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Avatar Photo</label>
                    <div
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = e.target.files?.[0];
                          if (file) genericUpload(file, 'avatarUrl');
                        };
                        input.click();
                      }}
                      onDragOver={preventDrag}
                      onDragEnter={preventDrag}
                      onDragLeave={preventDrag}
                      onDrop={(e) => handleFileDrop(e, 'avatar')}
                      className="aspect-square bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all group overflow-hidden relative"
                    >
                      {isUploading ? (
                        <Loader2 className="animate-spin text-black" />
                      ) : (
                        <>
                          {fields.avatarUrl ? (
                            <img
                              src={fields.avatarUrl}
                              alt="Avatar preview"
                              className="w-full h-full object-cover group-hover:opacity-50 transition-opacity"
                            />
                          ) : (
                            <span className="text-gray-400 text-xs font-bold uppercase">Upload</span>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Upload className="text-black" />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-6 mt-4">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Banner Image</label>
                  <div
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) genericUpload(file, 'bannerUrl');
                      };
                      input.click();
                    }}
                    onDragOver={preventDrag}
                    onDragEnter={preventDrag}
                    onDragLeave={preventDrag}
                    onDrop={(e) => handleFileDrop(e, 'bannerUrl')}
                    className="w-full h-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all group overflow-hidden relative"
                  >
                    {isUploading ? (
                      <Loader2 className="animate-spin text-black" />
                    ) : (
                      <>
                        {fields.bannerUrl ? (
                          <img
                            src={fields.bannerUrl}
                            alt="Banner preview"
                            className="w-full h-full object-cover group-hover:opacity-50 transition-opacity"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs font-bold uppercase">Upload Banner</span>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Upload className="text-black" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-6 mt-4">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Disclaimer Text</label>
                  <textarea
                    value={fields.disclaimer || ""}
                    onChange={(e) => handleFieldChange('disclaimer', e.target.value)}
                    className="w-full bg-gray-50 border-0 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all min-h-[80px] resize-none"
                  />
                </div>

                <div className="mb-6 mt-4">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Bottom Links (Separated by |)</label>
                  <input
                    type="text"
                    value={fields.bottomLinks || ""}
                    onChange={(e) => handleFieldChange('bottomLinks', e.target.value)}
                    placeholder="example.com | newsletter | store"
                    className="w-full bg-gray-50 border-0 rounded-xl h-12 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all"
                  />
                </div>

                {fieldOrder.map((key, index) => {
                  const value = fields[key];
                  return (
                    <div key={key} className="relative group/field">
                      <div className="flex justify-between items-center mb-1.5 ml-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        <div className="flex gap-1 opacity-0 group-hover/field:opacity-100 transition-opacity">
                          <button
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-20"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => moveField(index, 'down')}
                            disabled={index === fieldOrder.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-20"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="flex-1 bg-gray-50 border-0 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all"
                        />
                        {["fullName", "title", "email", "phone", "company"].indexOf(key) === -1 && (
                          <button
                            onClick={() => {
                              const newFields = { ...fields };
                              delete newFields[key];
                              setFields(newFields);
                              setFieldOrder(prev => prev.filter(k => k !== key));
                            }}
                            className="bg-red-50 text-red-500 p-3 rounded-xl hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover/field:opacity-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {fields.customFields && fields.customFields.map((cf, index) => (
                  <div key={cf.id} className="relative group/field mt-4 border border-transparent hover:border-gray-100 rounded-2xl p-1 -m-1 transition-colors">
                    <div className="flex justify-between items-center mb-1.5 ml-1">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400">
                        {cf.type === 'button' ? 'BUTTON: ' : ''}{cf.type === 'image' ? 'IMAGE: ' : ''}{cf.label}
                      </label>
                      <div className="flex gap-1 opacity-0 group-hover/field:opacity-100 transition-opacity bg-white/80 rounded-md">
                        {cf.type !== 'button' && (
                          <button
                            onClick={() => {
                              const updated = [...(fields.customFields || [])];
                              updated[index].showLabel = cf.showLabel === false ? true : false;
                              setFields(prev => ({ ...prev, customFields: updated }));
                            }}
                            className={`p-1 rounded transition-colors ${cf.showLabel !== false ? 'text-black hover:bg-gray-100' : 'text-gray-300 hover:text-black hover:bg-gray-100'}`}
                            title={cf.showLabel !== false ? 'Hide Label' : 'Show Label'}
                          >
                            <Type className="w-3 h-3" />
                          </button>
                        )}
                        {cf.type === 'text' && cf.icon !== 'none' && (
                          <button
                            onClick={() => {
                              const updated = [...(fields.customFields || [])];
                              updated[index].showIcon = cf.showIcon === false ? true : false;
                              setFields(prev => ({ ...prev, customFields: updated }));
                            }}
                            className={`p-1 rounded transition-colors ${cf.showIcon !== false ? 'text-black hover:bg-gray-100' : 'text-gray-300 hover:text-black hover:bg-gray-100'}`}
                            title={cf.showIcon !== false ? 'Hide Icon' : 'Show Icon'}
                          >
                           <ImageIcon className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (index > 0) {
                              const updated = [...(fields.customFields || [])];
                              const temp = updated[index - 1];
                              updated[index - 1] = updated[index];
                              updated[index] = temp;
                              setFields(prev => ({ ...prev, customFields: updated }));
                            }
                          }}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-black hover:bg-gray-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => {
                            if (fields.customFields && index < fields.customFields.length - 1) {
                              const updated = [...(fields.customFields || [])];
                              const temp = updated[index + 1];
                              updated[index + 1] = updated[index];
                              updated[index] = temp;
                              setFields(prev => ({ ...prev, customFields: updated }));
                            }
                          }}
                          disabled={!fields.customFields || index === fields.customFields.length - 1}
                          className="p-1 text-gray-400 hover:text-black hover:bg-gray-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                       <div className="flex-1 flex flex-col gap-2">
                        {cf.type === 'image' ? (
                          <div
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e) => handleCustomFileUpload(e, index);
                              input.click();
                            }}
                            onDragOver={preventDrag}
                            onDragEnter={preventDrag}
                            onDragLeave={preventDrag}
                            onDrop={(e) => handleFileDrop(e, index)}
                            className="w-full aspect-[3/1] bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all group overflow-hidden relative"
                          >
                            {uploadingCustomIndex === index ? (
                              <Loader2 className="animate-spin text-black" />
                            ) : cf.value ? (
                              <>
                                <img src={cf.value} className="h-full object-contain p-2 group-hover:opacity-50 transition-opacity" alt={cf.label} />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Upload className="text-black" />
                                </div>
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-black transition-colors">
                                <Upload className="w-5 h-5 mb-1" />
                                <span className="text-[10px] font-bold uppercase tracking-widest">Upload Image</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex-1 flex bg-gray-50 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-black transition-all">
                            {cf.icon && cf.icon !== 'none' && ICONS[cf.icon] && (
                              <div className="flex items-center justify-center pl-4 pr-2 text-gray-400" dangerouslySetInnerHTML={{ __html: ICONS[cf.icon]('#9ca3af') }} />
                            )}
                            <input
                              type="text"
                              value={cf.value}
                              onChange={(e) => {
                                const updated = [...(fields.customFields || [])];
                                updated[index].value = e.target.value;
                                setFields(prev => ({ ...prev, customFields: updated }));
                              }}
                              placeholder={cf.type === 'button' ? 'https://...' : 'Value...'}
                              className="flex-1 bg-transparent border-0 py-3 px-4 pl-2 text-sm outline-none"
                            />
                          </div>
                        )}
                        {cf.type === 'image' && (
                          <div className="flex gap-2">
                             <input
                                type="text"
                                placeholder="Width (e.g. 150)"
                                value={cf.imageWidth || ''}
                                onChange={(e) => {
                                  const updated = [...(fields.customFields || [])];
                                  updated[index].imageWidth = e.target.value;
                                  setFields(prev => ({ ...prev, customFields: updated }));
                                }}
                                className="flex-1 bg-gray-50 border-0 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-black outline-none"
                             />
                             <input
                                type="text"
                                placeholder="Height (e.g. 50)"
                                value={cf.imageHeight || ''}
                                onChange={(e) => {
                                  const updated = [...(fields.customFields || [])];
                                  updated[index].imageHeight = e.target.value;
                                  setFields(prev => ({ ...prev, customFields: updated }));
                                }}
                                className="flex-1 bg-gray-50 border-0 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-black outline-none"
                             />
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          const updated = fields.customFields?.filter((_, i) => i !== index);
                          setFields(prev => ({ ...prev, customFields: updated }));
                        }}
                        className="bg-red-50 text-red-500 p-3 rounded-xl hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover/field:opacity-100 h-[44px]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-gray-100">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Bottom Banner / Instructions</label>
                  <textarea
                    value={fields.banner}
                    onChange={(e) => handleFieldChange("banner", e.target.value)}
                    placeholder="Enter full-width instructions, banner or disclaimer here..."
                    className="w-full bg-gray-50 border-0 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all min-h-[100px] resize-none"
                  />
                </div>

                <button
                  onClick={() => setShowCustomFieldForm(true)}
                  className="w-full py-3 border-2 border-dashed border-gray-100 rounded-xl text-xs font-bold text-gray-400 hover:border-black hover:text-black transition-all flex items-center justify-center gap-2"
                >
                  + Add Custom Field or Button
                </button>
              </div>
            )}

            {activeTab === "templates" && (
              <div className="grid grid-cols-1 gap-4 overflow-x-hidden">
                {SIGNATURE_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => setTemplateId(template.id)}
                    className={`p-4 rounded-2xl border-2 transition-all text-left relative group/temp ${templateId === template.id ? "border-black bg-gray-50" : "border-gray-100 hover:border-gray-200"}`}
                  >
                    <div className="font-bold text-sm mb-1">{template.name}</div>
                    <div className="scale-[0.4] origin-top-left h-12 overflow-hidden opacity-50 grayscale group-hover/temp:grayscale-0 group-hover/temp:opacity-100 transition-all">
                      <div dangerouslySetInnerHTML={{ __html: template.render(fields, colors, fieldOrder) }} />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {activeTab === "style" && (
              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Primary Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={colors.primary} onChange={(e) => setColors({ ...colors, primary: e.target.value })} className="w-10 h-10 border-0 p-0 rounded-lg cursor-pointer" />
                    <input type="text" value={colors.primary} onChange={(e) => setColors({ ...colors, primary: e.target.value })} className="flex-1 bg-gray-50 border-0 rounded-xl px-4 text-sm font-mono" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Secondary Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={colors.secondary} onChange={(e) => setColors({ ...colors, secondary: e.target.value })} className="w-10 h-10 border-0 p-0 rounded-lg cursor-pointer" />
                    <input type="text" value={colors.secondary} onChange={(e) => setColors({ ...colors, secondary: e.target.value })} className="flex-1 bg-gray-50 border-0 rounded-xl px-4 text-sm font-mono" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Accent Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={colors.accent} onChange={(e) => setColors({ ...colors, accent: e.target.value })} className="w-10 h-10 border-0 p-0 rounded-lg cursor-pointer" />
                    <input type="text" value={colors.accent} onChange={(e) => setColors({ ...colors, accent: e.target.value })} className="flex-1 bg-gray-50 border-0 rounded-xl px-4 text-sm font-mono" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <AnimatePresence>
            {showCustomFieldForm && (
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="absolute inset-0 bg-white z-20 flex flex-col"
              >
                <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                  <button onClick={() => setShowCustomFieldForm(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-xs font-black uppercase tracking-widest text-gray-800">Add Custom Field</h3>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50 flex flex-col gap-6">
                  <div className="flex gap-2 p-1 bg-gray-200/50 rounded-xl">
                    <button
                      onClick={() => setNewField({ ...newField, type: 'text' })}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${newField.type === 'text' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Text
                    </button>
                    <button
                      onClick={() => setNewField({ ...newField, type: 'button' })}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${newField.type === 'button' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Button
                    </button>
                    <button
                      onClick={() => setNewField({ ...newField, type: 'image' })}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${newField.type === 'image' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Image
                    </button>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                        {newField.type === 'button' ? 'Button Label' : newField.type === 'image' ? 'Image Alt Text / Label' : 'Field Name'}
                      </label>
                      <input
                        type="text"
                        placeholder={newField.type === 'button' ? 'e.g. Visit Website' : newField.type === 'image' ? 'e.g. Award Badge' : 'e.g. Office'}
                        value={newField.label}
                        onChange={e => setNewField({ ...newField, label: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-2xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all shadow-sm"
                      />
                    </div>
                    {newField.type === 'image' ? (
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                          Upload Image
                        </label>
                        <div
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => handleCustomFileUpload(e);
                            input.click();
                          }}
                          onDragOver={preventDrag}
                          onDragEnter={preventDrag}
                          onDragLeave={preventDrag}
                          onDrop={(e) => handleFileDrop(e, 'new-custom')}
                          className="w-full aspect-[3/1] bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-all group overflow-hidden relative mb-4"
                        >
                          {isUploading ? (
                            <Loader2 className="animate-spin text-black" />
                          ) : newField.value ? (
                            <>
                              <img src={newField.value} className="h-full object-contain p-2 group-hover:opacity-50 transition-opacity" alt={newField.label} />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Upload className="text-black" />
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-black transition-colors">
                              <Upload className="w-5 h-5 mb-1" />
                              <span className="text-[10px] font-bold uppercase tracking-widest">Upload Image</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                          {newField.type === 'button' ? 'URL Link' : 'Value'}
                        </label>
                        <input
                          type="text"
                          placeholder={newField.type === 'button' ? 'https://...' : 'e.g. New York'}
                          value={newField.value}
                          onChange={e => setNewField({ ...newField, value: e.target.value })}
                          className="w-full bg-white border border-gray-200 rounded-2xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all shadow-sm"
                        />
                      </div>
                    )}
                    {newField.type === 'image' && (
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Width</label>
                          <input
                            type="text"
                            placeholder="e.g. 150"
                            value={newField.imageWidth}
                            onChange={e => setNewField({ ...newField, imageWidth: e.target.value })}
                            className="w-full bg-white border border-gray-200 rounded-2xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all shadow-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Height</label>
                          <input
                            type="text"
                            placeholder="e.g. 50"
                            value={newField.imageHeight}
                            onChange={e => setNewField({ ...newField, imageHeight: e.target.value })}
                            className="w-full bg-white border border-gray-200 rounded-2xl py-3 px-4 text-sm focus:ring-2 focus:ring-black outline-none transition-all shadow-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {newField.type !== 'image' && (
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Display Icon</label>
                      <div className="grid grid-cols-5 gap-2">
                        <button
                          onClick={() => setNewField({ ...newField, icon: 'none' })}
                          className={`aspect-square rounded-2xl border-2 flex items-center justify-center transition-all shadow-sm ${newField.icon === 'none' ? 'border-black bg-black text-white' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-300'}`}
                        >
                          <span className="text-xs font-bold">None</span>
                        </button>
                        {Object.keys(ICONS).map(iconName => (
                          <button
                            key={iconName}
                            onClick={() => setNewField({ ...newField, icon: iconName })}
                            className={`aspect-square border-2 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all shadow-sm ${newField.icon === iconName ? 'border-black bg-black text-white' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-300'}`}
                            title={iconName}
                          >
                            <div dangerouslySetInnerHTML={{ __html: ICONS[iconName](newField.icon === iconName ? '#ffffff' : '#9ca3af') }} className="flex items-center justify-center scale-125" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {newField.type === 'button' && (
                    <div className="space-y-5 pt-5 border-t border-gray-200 mt-2">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Button Style</label>
                        <div className="flex gap-2">
                          {['solid', 'outline', 'link'].map(style => (
                            <button
                              key={style}
                              onClick={() => setNewField({ ...newField, buttonStyle: style })}
                              className={`flex-1 py-2 text-xs font-bold capitalize rounded-xl border-2 shadow-sm ${newField.buttonStyle === style ? 'border-black bg-black text-white' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-300'}`}
                            >
                              {style}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Button Color</label>
                          <div className="flex gap-2 items-center bg-white border border-gray-200 rounded-2xl p-2 shadow-sm">
                            <input type="color" value={newField.buttonColor} onChange={e => setNewField({ ...newField, buttonColor: e.target.value })} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                            <input type="text" value={newField.buttonColor} onChange={e => setNewField({ ...newField, buttonColor: e.target.value })} className="flex-1 bg-transparent border-0 px-2 text-xs font-mono outline-none" />
                          </div>
                        </div>
                        {newField.buttonStyle === 'solid' && (
                          <div className="flex-1">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">Text Color</label>
                            <div className="flex gap-2 items-center bg-white border border-gray-200 rounded-2xl p-2 shadow-sm">
                              <input type="color" value={newField.buttonTextColor} onChange={e => setNewField({ ...newField, buttonTextColor: e.target.value })} className="w-8 h-8 rounded border-0 p-0 cursor-pointer" />
                              <input type="text" value={newField.buttonTextColor} onChange={e => setNewField({ ...newField, buttonTextColor: e.target.value })} className="flex-1 bg-transparent border-0 px-2 text-xs font-mono outline-none" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-auto pt-6">
                    <button
                      onClick={() => {
                        if (!newField.label || !newField.value) return;
                        const newCustomField = {
                          id: Date.now().toString(),
                          type: newField.type,
                          label: newField.label,
                          value: newField.value,
                          icon: newField.icon,
                          buttonStyle: newField.buttonStyle,
                          buttonColor: newField.buttonColor,
                          buttonTextColor: newField.buttonTextColor,
                          showLabel: newField.showLabel !== false,
                          showIcon: newField.showIcon !== false,
                          imageWidth: newField.imageWidth,
                          imageHeight: newField.imageHeight,
                        };
                        setFields(prev => ({
                          ...prev,
                          customFields: [...(prev.customFields || []), newCustomField],
                        }));
                        setShowCustomFieldForm(false);
                        setNewField(EMPTY_NEW_FIELD);
                      }}
                      disabled={!newField.label || !newField.value}
                      className="w-full bg-black text-white py-4 rounded-2xl text-sm font-bold shadow-[0_4px_14px_rgba(0,0,0,0.25)] hover:bg-gray-900 transition-all disabled:opacity-50 disabled:shadow-none"
                    >
                      Save Field
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Preview Area */}
        <div className="flex-1 bg-white rounded-3xl border border-gray-100 p-8 flex flex-col gap-6 relative shadow-sm">
          <div className="flex justify-center gap-2 bg-gray-50 p-1 rounded-2xl self-center">
            {[
              { id: "desktop", icon: Monitor },
              { id: "tablet", icon: Tablet },
              { id: "mobile", icon: Smartphone },
            ].map(mode => (
              <button
                key={mode.id}
                onClick={() => setPreviewMode(mode.id)}
                className={`p-2 rounded-xl transition-all ${previewMode === mode.id ? "bg-white shadow-sm text-black" : "text-gray-400 hover:text-gray-600"}`}
              >
                <mode.icon className="w-5 h-5" />
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 rounded-2xl p-4 overflow-auto border border-gray-100 border-dashed relative">
            <div className="flex gap-2 mb-4">
              <button
                onClick={copySignature}
                className="bg-white text-black border border-gray-200 px-4 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shadow-sm transition-all hover:scale-[1.02]"
              >
                {copyStatus === "signature" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copyStatus === "signature" ? "Copied!" : "Copy Signature"}
              </button>
              <button
                onClick={copyHTMLCode}
                className="bg-white text-black border border-gray-200 px-4 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shadow-sm transition-all hover:scale-[1.02]"
              >
                {copyStatus === "code" ? <Check className="w-4 h-4 text-green-500" /> : <Code className="w-4 h-4" />}
                {copyStatus === "code" ? "HTML Copied!" : "Copy HTML Code"}
              </button>
            </div>

            <motion.div
              layout
              className={`bg-white p-8 rounded-xl shadow-xl transition-all duration-500 overflow-hidden ${
                previewMode === "desktop" ? "w-[600px]" : previewMode === "tablet" ? "w-[400px]" : "w-[300px]"
              }`}
            >
              <div dangerouslySetInnerHTML={{ __html: currentTemplate.render(fields, colors, fieldOrder) }} />
            </motion.div>
          </div>

          <p className="text-center text-xs text-gray-400 font-medium tracking-widest uppercase">
            Previewing: {currentTemplate.name}
          </p>
        </div>
      </div>
    </div>
  );
}
